import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bed, Bath, Maximize } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { optimizedImageUrl, optimizedSrcSet } from '@/lib/imageOptimizer';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';
import { propertyUrl } from '@/lib/listingUrl';
import { CompactContactActions } from './CompactContactActions';


interface Props {
  property: any;
}

export function RecommendedPropertyCard({ property }: Props) {
  const { i18n } = useTranslation();
  const { displayCurrency, rate } = useCurrency();

  const lang = i18n.language || 'en';
  const pickLocalized = (v: any) =>
    v && typeof v === 'object' ? v[lang] || v.en || v.am || v.om || '' : v || '';
  const title = pickLocalized(property.title) || pickLocalized(property.name);

  const images = property.property_images || [];
  const primary =
    images.find((img: any) => img.is_primary)?.url ||
    images[0]?.url ||
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop';
  const img = optimizedImageUrl(primary, { width: 480, height: 320, quality: 75 });
  const imgSrcSet = optimizedSrcSet(primary, { width: 480, height: 320, quality: 75 });

  const timeAgo = property.created_at
    ? formatDistanceToNow(new Date(property.created_at), { addSuffix: false })
    : '';
  const formatted = fmtPrice(property.price, property.currency || 'ETB', displayCurrency, rate);
  const locText = [property.city, property.sub_city].filter(Boolean).join(', ');
  const typeLabel = `${property.property_type || 'Property'} ${
    property.listing_type === 'rent' ? 'For Rent' : 'For Sale'
  }`;

  return (
    <Link
      to={propertyUrl(property)}
      className="group block bg-card rounded-xl border overflow-hidden hover:shadow-lg hover:border-primary/40 transition-all"
    >
      <div className="relative aspect-[5/4] bg-muted overflow-hidden">
        <img
          src={img}
          srcSet={imgSrcSet}
          alt={title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {timeAgo && (
          <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide">
            {timeAgo} ago
          </span>
        )}
      </div>

      <div className="px-3 pt-2 pb-2.5">
        <div className="font-bold text-sm leading-tight truncate">{formatted}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{locText || 'Ethiopia'}</div>
        <div className="text-[11px] font-medium text-primary mt-0.5 truncate">{typeLabel}</div>

        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
          {property.area_sqm != null && (
            <span className="flex items-center gap-1"><Maximize className="h-3 w-3" />{property.area_sqm} m²</span>
          )}
          {property.bedrooms != null && (
            <span className="flex items-center gap-1"><Bed className="h-3 w-3" />{property.bedrooms}</span>
          )}
          {property.bathrooms != null && (
            <span className="flex items-center gap-1"><Bath className="h-3 w-3" />{property.bathrooms}</span>
          )}
        </div>

        <div className="pt-2 mt-1.5 border-t">
          <CompactContactActions
            phone={property.contact_phone}
            agentName={property.contact_person}
            agencyName={property.agency_name}
            agentLogo={property.agency_logo_url}
            referenceId={property.reference_code || `ID${String(property.id).slice(0, 8)}`}
            propertyId={property.id}
            listingType="property"
          />
        </div>
      </div>
    </Link>
  );
}

